import { MedplumClient } from "@medplum/core";
import { DocumentReference } from "@medplum/fhirtypes";

import { Group, Paper, Text, UnstyledButton } from "@mantine/core";

import { IconEdit, IconTrash } from "@tabler/icons-react";

import { UploadedFileData } from "@/file-uploads/types/file-uploads";
import { parseDocumentReferenceFields } from "@/file-uploads/utils/file-uploads";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";

/** Stable data-testid segment from file display name (matches Cypress slugify). */
function fileNameTestIdSegment(fileName: string | undefined): string {
  const raw = (fileName || "unnamed").toLowerCase();
  return raw
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

interface FileRowProps {
  medplum: MedplumClient;
  documentReference: DocumentReference;
  onEdit: (file: DocumentReference) => void;
  onDelete: (file: DocumentReference) => void;
  isEncounterActive: boolean;
  isAmendment: boolean;
}

export default function FileRow({
  medplum,
  documentReference,
  onEdit,
  onDelete,
  isEncounterActive,
  isAmendment,
}: FileRowProps) {
  const { hasProviderOrScribeRole } = usePractitionerPermissions();
  const fileData: UploadedFileData = parseDocumentReferenceFields(documentReference);
  const { name, type, description, dateAdded } = fileData;

  const editable = (isEncounterActive || isAmendment) && hasProviderOrScribeRole;

  return (
    <Paper
      p="md"
      withBorder
      style={{
        background: "white",
        marginBottom: "8px",
      }}
    >
      <Group wrap="nowrap" align="center" justify="space-between">
        {/* File Name */}
        <Group gap="xs" style={{ minWidth: "150px", flexShrink: 0 }}>
          <Text fw={500} size="sm" c="dark.6">
            {name || "Unnamed File"}
          </Text>
        </Group>

        {/* Type */}
        <Text size="sm" c="dimmed" style={{ minWidth: "100px", flexShrink: 0 }}>
          {type || "Unknown"}
        </Text>

        {/* Description */}
        <Text size="sm" c="dimmed" style={{ minWidth: "150px", flexShrink: 0 }}>
          {description || "No description"}
        </Text>

        {/* Date Added */}
        <Text size="sm" c="dimmed" style={{ minWidth: "100px", flexShrink: 0 }}>
          {dateAdded || "Unknown date"}
        </Text>

        {/* Actions */}
        {editable && (
          <UnstyledButton onClick={() => onDelete(documentReference)}>
            <IconTrash size={16} color="red" />
          </UnstyledButton>
        )}

        {editable && (
          <UnstyledButton
            onClick={() => onEdit(documentReference)}
            data-testid={`encounter-file-edit-${fileNameTestIdSegment(name)}`}
          >
            <Group gap="xs">
              <IconEdit size={16} />
              Edit
            </Group>
          </UnstyledButton>
        )}
      </Group>
    </Paper>
  );
}
